import { useCallback, useEffect, useRef, useState } from "react";
import { fetchHistoricalOrderMasterData } from "../../../../../../../services/apis";

// Module-level cache, shared across every component that uses this hook
// (HistoricalOrderBulkUpload and AddCompletedOrder both need the same real
// companies/projects/commodities/etc.) — mirrors the BE's own 10-minute TTL
// cache so re-opening either modal doesn't refetch every time.
let cachedData = null;
let cachedAt = 0;
const CACHE_TTL_MS = 10 * 60 * 1000;

/**
 * Loads the real master data from GET /v1/historical-orders/master-data
 * (companies, projects, commodities, packages, labs, logistics, options, ...).
 * Replaces the earlier hardcoded 2-company MOCK_MASTER_DATA placeholder now
 * that this endpoint is real and working.
 */
export default function useHistoricalOrderMasterData() {
  const [data, setData] = useState(cachedData);
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const load = useCallback((forceRefresh = false) => {
    if (!forceRefresh && cachedData && Date.now() - cachedAt < CACHE_TTL_MS) {
      setData(cachedData);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetchHistoricalOrderMasterData({ forceRefresh })
      .then((response) => {
        const payload = response?.data?.data;
        cachedData = payload;
        cachedAt = Date.now();
        if (mountedRef.current) setData(payload);
      })
      .catch((err) => {
        if (mountedRef.current) {
          setError(err?.response?.data?.message || err.message || "Failed to load master data");
        }
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load(false);
    return () => { mountedRef.current = false; };
  }, [load]);

  return { data, loading, error, refresh: () => load(true) };
}
